/* eslint-disable @typescript-eslint/no-explicit-any */
import { operators as o } from '@pendo/aggregations';
import { queueAggregationRequest, sendAggregation } from '@pendo/agg-connect';
import { v4 as uuid } from 'uuid';
import { MS_IN_TIME_UNITS } from '@/utils/time.utils';
import find from 'lodash/find';
import has from 'lodash/has';

export { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@pendo/middleware';

// Don't use this.
// Use {connectAggregationToState} in @pendo/agg-connect
// Or use the AggConnect angular service
export async function runAggregation (aggregation: any, rowsOnly: boolean = true) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { value } = await queueAggregationRequest(aggregation).toPromise();

    return rowsOnly ? value.data[value.periods[0]] : value;
}

export function rowLimitUIModifier (sourceObj: any, ROW_LIMIT?: number) {
    if (!ROW_LIMIT) return sourceObj;
    const { pipeline } = sourceObj;
    let newPipeline = pipeline;
    if (!find(pipeline, (operator) => has(operator, 'limit'))) {
        newPipeline = [
            ...pipeline,
            {
                limit: ROW_LIMIT,
            },
        ];
    }

    return {
        ...sourceObj,
        pipeline: newPipeline,
    };
}

export function getColumnsForAggregation (columns: Array<any>) {
    return columns.map((column) => {
        // connect.js throws error if formatter is passed here
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { poll, formatter, ...rest } = column;

        let { functionName = 'metadataField' } = column;
        if (column.fact) {
            functionName = ['daysActive', 'eventCount', 'eventTime', 'guide'].includes(column.type)
                ? column.fact
                : `${column.type}_${column.fact}`;
        }

        if (poll) {
            functionName = 'pollResponse';
            rest.idResponses = poll.idResponses;
            rest.type = poll.attributes.type;
            rest.numericResponses = poll.numericResponses;
        }

        return {
            ...rest,
            fieldName: column.field,
            primaryKeys: ['visitorId'],
            columnLabel: column.label,
            columnName: column.prop,
            functionName,
        };
    });
}

export function getAddedColumnsForAggregation (columns: Array<any>) {
    const filteredColumns = columns.filter((column) => column.prop && !column.immutable);

    return getColumnsForAggregation(filteredColumns);
}

export function getJoinKeysForAggregation (columns: Array<any>) {
    const addedColumns = getAddedColumnsForAggregation(columns);
    const keys = addedColumns.map((column) => `${column.kind}Id`).concat('visitorId');

    return [...new Set([...keys])];
}

export function runAggregationOnce (aggregation: any, state: any = {}, streamId: string = '') {
    if (!streamId) {
        streamId = uuid();
    }

    return new Promise((resolve, reject) => {
        const response = sendAggregation(aggregation, streamId, state, undefined);

        response.subscribe(({ value, status, error }: { value: any; status: any; error: any }) => {
            if (status === 'resolved') {
                if (typeof response?.unsubscribe === 'function') response.unsubscribe();
                if (typeof response?.disconnect === 'function') response.disconnect();
                resolve(value);
            }

            if (status === 'rejected') {
                if (typeof response?.unsubscribe === 'function') response.unsubscribe();
                if (typeof response?.disconnect === 'function') response.disconnect();
                reject(error);
            }
        });
    });
}

export function sumMinutesToMS (field: string = 'numMinutes') {
    return o.sum(`${field} * ${MS_IN_TIME_UNITS.MINUTE}`);
}
